import type { AuthUser, UserRole } from "./auth";
import { roleAllows } from "./auth";

/**
 * 書込操作のカテゴリ。API 側の role ガード（require_write_role）と対応させる。
 *
 * - `master`        : マスタ編集（取引先など）→ ADMIN
 * - `transactional` : 取引（見積・案件・図面）→ LEADER
 * - `workReport`    : 工数・作業実績 → OPERATOR
 * - `userAdmin`     : ユーザー管理 → ADMIN
 *
 * ngtg_msc の utils/authorization.dart（WriteScope）に対応する。
 */
export type WriteScope = "master" | "transactional" | "workReport" | "userAdmin";

/** scope の書込に必要な最小 role（API 側のカテゴリ→role と一致させる）。 */
const REQUIRED_ROLE: Record<WriteScope, UserRole> = {
  master: "admin",
  userAdmin: "admin",
  transactional: "leader",
  workReport: "operator",
};

export function requiredRoleFor(scope: WriteScope): UserRole {
  return REQUIRED_ROLE[scope];
}

/**
 * user が scope の書込を実行できるか。未ログインは不可。
 *
 * **権限判定は API が最終的な境界**。これはボタンの無効化やメニューの非表示
 * といった UI 出し分けのための補助にすぎない。
 */
export function canWrite(user: AuthUser | null, scope: WriteScope): boolean {
  if (!user) return false;
  return roleAllows(user.role, requiredRoleFor(scope));
}

/** 権限不足時に表示する共通メッセージ。 */
export function unauthorizedMessage(scope: WriteScope): string {
  const label: Record<UserRole, string> = {
    admin: "管理者",
    leader: "リーダー以上",
    operator: "オペレーター以上",
    view: "閲覧",
  };
  return `${label[requiredRoleFor(scope)]}の権限が必要です`;
}
